import { createHttpService } from "../ApiService";
import {
  TAcademic,
  TCourse,
  TCreateCourseRequest,
  TUpdateCourseRequest,
  TCourseListQuery,
  TPaginatedCoursesResponse,
  TClass,
  TCreateClassRequest,
  TUpdateClassRequest,
  TClassListQuery,
  TPaginatedClassesResponse,
  TGradeLevel,
  TCreateGradeLevelRequest,
  TUpdateGradeLevelRequest,
  TSyllabus,
  TCreateSyllabusRequest,
  TUpdateSyllabusRequest,
  TSubject,
  TCreateSubjectRequest,
  TUpdateSubjectRequest,
  TSubjectListQuery,
  TPaginatedSubjectsResponse,
  TSection,
  TCreateSectionRequest,
  TUpdateSectionRequest,
  TClassSubject,
  TAssignClassSubjectRequest,
  TUpdateClassSubjectRequest,
  TSectionTeacher,
} from "./academic.interfaces";
import { TInternalUser } from "../users/users.interfaces";
import {
  academicListMock,
  courseListMock,
  classListMock,
  gradeLevelListMock,
  syllabusListMock,
  subjectListMock,
  sectionListMock,
  classSubjectListMock,
  sectionTeachersMock,
} from "../../__mocks__/academic/academicMock";
import { mergePaginationQuery } from "../common.interfaces";

function appendQuery(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  if (!params) return path;
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.append(k, String(v));
  });
  const q = usp.toString();
  return q ? `${path}?${q}` : path;
}

function paginateCourses(
  items: TCourse[],
  q?: TCourseListQuery,
): TPaginatedCoursesResponse {
  const merged = mergePaginationQuery({
    page: q?.page,
    limit: q?.limit ?? 100,
  });
  let list = [...items];
  if (q?.obepId) list = list.filter((c) => c.obepId === q.obepId);
  if (q?.status) list = list.filter((c) => c.status === q.status);
  if (q?.search) {
    const s = q.search.toLowerCase();
    list = list.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        (c.description?.toLowerCase().includes(s) ?? false),
    );
  }
  const total = list.length;
  const start = (merged.page - 1) * merged.limit;
  const data = list.slice(start, start + merged.limit);
  return { data, total, page: merged.page, limit: merged.limit };
}

function paginateClasses(
  items: TClass[],
  q?: TClassListQuery,
): TPaginatedClassesResponse {
  const merged = mergePaginationQuery({
    page: q?.page,
    limit: q?.limit ?? 100,
  });
  let list = [...items];
  if (q?.courseId) list = list.filter((c) => c.courseId === q.courseId);
  if (q?.academicYear) list = list.filter((c) => c.academicYear === q.academicYear);
  if (q?.search) {
    const s = q.search.toLowerCase();
    list = list.filter(
      (c) =>
        c.name.toLowerCase().includes(s) || c.grade.toLowerCase().includes(s),
    );
  }
  const total = list.length;
  const start = (merged.page - 1) * merged.limit;
  const data = list.slice(start, start + merged.limit);
  return { data, total, page: merged.page, limit: merged.limit };
}

function paginateSubjects(
  items: TSubject[],
  q?: TSubjectListQuery,
): TPaginatedSubjectsResponse {
  const merged = mergePaginationQuery({
    page: q?.page,
    limit: q?.limit ?? 100,
  });
  let list = [...items];
  if (q?.isElective !== undefined)
    list = list.filter((s) => s.isElective === q.isElective);
  if (q?.search) {
    const s = q.search.toLowerCase();
    list = list.filter(
      (sub) =>
        sub.name.toLowerCase().includes(s) ||
        sub.code.toLowerCase().includes(s),
    );
  }
  const total = list.length;
  const start = (merged.page - 1) * merged.limit;
  const data = list.slice(start, start + merged.limit);
  return { data, total, page: merged.page, limit: merged.limit };
}

export const useAcademicService = () => {
  const httpAPI = createHttpService();
  const baseAcademicPath = "";
  const adminSyllabiPath = "/syllabi";

  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  const getAcademics = () => {
    if (appWithMockData) {
      return Promise.resolve(academicListMock);
    }
    return httpAPI.academic.get<TAcademic[]>(`${baseAcademicPath}`);
  };

  const getCourses = (params?: TCourseListQuery) => {
    if (appWithMockData) {
      return Promise.resolve(paginateCourses(courseListMock, params));
    }
    return httpAPI.academic.get<TPaginatedCoursesResponse>(
      appendQuery(`${baseAcademicPath}/courses`, params),
    );
  };

  const getCourseById = (id: string) => {
    if (appWithMockData) {
      const course = courseListMock.find((c) => c.id === id);
      if (!course) return Promise.reject(new Error("Course not found"));
      return Promise.resolve(course);
    }
    return httpAPI.academic.get<TCourse>(`${baseAcademicPath}/courses/${id}`);
  };

  const createCourse = (request: TCreateCourseRequest) => {
    if (appWithMockData) {
      const now = new Date().toISOString();
      const newCourse: TCourse = {
        id: `course-${Date.now()}`,
        instituteId: "institute-1",
        obepId: request.obepId,
        name: request.name,
        description: request.description,
        startGrade: request.startGrade,
        endGrade: request.endGrade,
        status: request.status ?? "active",
        createdAt: now,
        updatedAt: now,
      };
      courseListMock.push(newCourse);
      return Promise.resolve(newCourse);
    }
    return httpAPI.academic.post<TCourse>(
      `${baseAcademicPath}/courses`,
      request,
    );
  };

  const updateCourse = (id: string, request: TUpdateCourseRequest) => {
    if (appWithMockData) {
      const index = courseListMock.findIndex((course) => course.id === id);
      if (index === -1) return Promise.reject(new Error("Course not found"));
      const updated: TCourse = {
        ...courseListMock[index],
        ...request,
        updatedAt: new Date().toISOString(),
      };
      courseListMock[index] = updated;
      return Promise.resolve(updated);
    }
    return httpAPI.academic.patch<TCourse>(
      `${baseAcademicPath}/courses/${id}`,
      request,
    );
  };

  const deleteCourse = (id: string) => {
    if (appWithMockData) {
      const index = courseListMock.findIndex((course) => course.id === id);
      if (index === -1) return Promise.reject(new Error("Course not found"));
      courseListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(`${baseAcademicPath}/courses/${id}`);
  };

  const getClasses = (params?: TClassListQuery) => {
    if (appWithMockData) {
      return Promise.resolve(paginateClasses(classListMock, params));
    }
    return httpAPI.academic.get<TPaginatedClassesResponse>(
      appendQuery(`${baseAcademicPath}/classes`, params),
    );
  };

  const getClassById = (id: string) => {
    if (appWithMockData) {
      const classItem = classListMock.find((c) => c.id === id);
      if (!classItem) return Promise.reject(new Error("Class not found"));
      return Promise.resolve(classItem);
    }
    return httpAPI.academic.get<TClass>(`${baseAcademicPath}/classes/${id}`);
  };

  const createClass = (courseId: string, request: TCreateClassRequest) => {
    if (appWithMockData) {
      const now = new Date().toISOString();
      const id = `class-${Date.now()}`;
      const defaultSectionId = `section-${id}-a`;
      const newClass: TClass = {
        id,
        instituteId: "institute-1",
        courseId,
        name: request.name,
        grade: request.grade,
        academicYear: request.academicYear,
        strength: request.strength,
        defaultSectionId,
        createdAt: now,
        updatedAt: now,
      };
      classListMock.push(newClass);
      sectionListMock.push({
        id: defaultSectionId,
        instituteId: "institute-1",
        classId: id,
        name: "A",
        createdAt: now,
        updatedAt: now,
      });
      return Promise.resolve(newClass);
    }
    return httpAPI.academic.post<TClass>(
      `${baseAcademicPath}/courses/${courseId}/classes`,
      request,
    );
  };

  const updateClass = (id: string, request: TUpdateClassRequest) => {
    if (appWithMockData) {
      const index = classListMock.findIndex((cls) => cls.id === id);
      if (index === -1) return Promise.reject(new Error("Class not found"));
      const updated: TClass = {
        ...classListMock[index],
        ...request,
        updatedAt: new Date().toISOString(),
      };
      classListMock[index] = updated;
      return Promise.resolve(updated);
    }
    return httpAPI.academic.patch<TClass>(
      `${baseAcademicPath}/classes/${id}`,
      request,
    );
  };

  const deleteClass = (id: string) => {
    if (appWithMockData) {
      const index = classListMock.findIndex((cls) => cls.id === id);
      if (index === -1) return Promise.reject(new Error("Class not found"));
      classListMock.splice(index, 1);
      for (let i = sectionListMock.length - 1; i >= 0; i--) {
        if (sectionListMock[i].classId === id) sectionListMock.splice(i, 1);
      }
      for (let i = classSubjectListMock.length - 1; i >= 0; i--) {
        if (classSubjectListMock[i].classId === id)
          classSubjectListMock.splice(i, 1);
      }
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(`${baseAcademicPath}/classes/${id}`);
  };

  const scoringBase = `${baseAcademicPath}/scoring-rubrics`;

  const getGradeLevels = (instituteId?: string) => {
    if (appWithMockData) {
      if (instituteId) {
        return Promise.resolve(
          gradeLevelListMock.filter((g) => g.instituteId === instituteId),
        );
      }
      return Promise.resolve(gradeLevelListMock);
    }
    const path = instituteId
      ? appendQuery(scoringBase, { instituteId })
      : scoringBase;
    return httpAPI.academic.get<TGradeLevel[]>(path);
  };

  const getGradeLevelById = (id: string) => {
    if (appWithMockData) {
      const gradeLevel = gradeLevelListMock.find((g) => g.id === id);
      if (!gradeLevel)
        return Promise.reject(new Error("Scoring rubric not found"));
      return Promise.resolve(gradeLevel);
    }
    return httpAPI.academic.get<TGradeLevel>(`${scoringBase}/${id}`);
  };

  const createGradeLevel = (request: TCreateGradeLevelRequest) => {
    if (appWithMockData) {
      const newGradeLevel: TGradeLevel = {
        id: `grade-${Date.now()}`,
        name: request.name,
        minPercentage: request.minPercentage,
        maxPercentage: request.maxPercentage,
        minScore: request.minScore,
        maxScore: request.maxScore,
        description: request.description,
        instituteId: request.instituteId,
      };
      gradeLevelListMock.push(newGradeLevel);
      return Promise.resolve(newGradeLevel);
    }
    return httpAPI.academic.post<TGradeLevel>(scoringBase, request);
  };

  const updateGradeLevel = (id: string, request: TUpdateGradeLevelRequest) => {
    if (appWithMockData) {
      const index = gradeLevelListMock.findIndex((g) => g.id === id);
      if (index === -1)
        return Promise.reject(new Error("Scoring rubric not found"));
      const updatedGradeLevel: TGradeLevel = {
        ...gradeLevelListMock[index],
        ...request,
      };
      gradeLevelListMock[index] = updatedGradeLevel;
      return Promise.resolve(updatedGradeLevel);
    }
    return httpAPI.academic.patch<TGradeLevel>(`${scoringBase}/${id}`, request);
  };

  const deleteGradeLevel = (id: string) => {
    if (appWithMockData) {
      const index = gradeLevelListMock.findIndex((g) => g.id === id);
      if (index === -1)
        return Promise.reject(new Error("Scoring rubric not found"));
      gradeLevelListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(`${scoringBase}/${id}`);
  };

  const getSubjects = (params?: TSubjectListQuery) => {
    if (appWithMockData) {
      return Promise.resolve(paginateSubjects(subjectListMock, params));
    }
    return httpAPI.academic.get<TPaginatedSubjectsResponse>(
      appendQuery(`${baseAcademicPath}/subjects`, params),
    );
  };

  const getSubjectById = (id: string) => {
    if (appWithMockData) {
      const s = subjectListMock.find((x) => x.id === id);
      if (!s) return Promise.reject(new Error("Subject not found"));
      return Promise.resolve(s);
    }
    return httpAPI.academic.get<TSubject>(`${baseAcademicPath}/subjects/${id}`);
  };

  const createSubject = (request: TCreateSubjectRequest) => {
    if (appWithMockData) {
      const now = new Date().toISOString();
      const row: TSubject = {
        id: `subject-${Date.now()}`,
        instituteId: "institute-1",
        name: request.name,
        code: request.code,
        description: request.description,
        subjectType: request.subjectType,
        isElective: request.isElective ?? false,
        createdAt: now,
        updatedAt: now,
      };
      subjectListMock.push(row);
      return Promise.resolve(row);
    }
    return httpAPI.academic.post<TSubject>(
      `${baseAcademicPath}/subjects`,
      request,
    );
  };

  const updateSubject = (id: string, request: TUpdateSubjectRequest) => {
    if (appWithMockData) {
      const index = subjectListMock.findIndex((s) => s.id === id);
      if (index === -1) return Promise.reject(new Error("Subject not found"));
      const updated: TSubject = {
        ...subjectListMock[index],
        ...request,
        updatedAt: new Date().toISOString(),
      };
      subjectListMock[index] = updated;
      return Promise.resolve(updated);
    }
    return httpAPI.academic.patch<TSubject>(
      `${baseAcademicPath}/subjects/${id}`,
      request,
    );
  };

  const deleteSubject = (id: string) => {
    if (appWithMockData) {
      const index = subjectListMock.findIndex((s) => s.id === id);
      if (index === -1) return Promise.reject(new Error("Subject not found"));
      subjectListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(
      `${baseAcademicPath}/subjects/${id}`,
    );
  };

  const listSectionsByClass = (classId: string) => {
    if (appWithMockData) {
      return Promise.resolve(
        sectionListMock.filter((s) => s.classId === classId),
      );
    }
    return httpAPI.academic.get<TSection[]>(
      `${baseAcademicPath}/classes/${classId}/sections`,
    );
  };

  const listSections = (classId?: string) => {
    if (appWithMockData) {
      if (classId)
        return Promise.resolve(
          sectionListMock.filter((s) => s.classId === classId),
        );
      return Promise.resolve([...sectionListMock]);
    }
    return httpAPI.academic.get<TSection[]>(
      appendQuery(`${baseAcademicPath}/sections`, classId ? { classId } : {}),
    );
  };

  const getSectionById = (id: string) => {
    if (appWithMockData) {
      const sec = sectionListMock.find((s) => s.id === id);
      if (!sec) return Promise.reject(new Error("Section not found"));
      return Promise.resolve(sec);
    }
    return httpAPI.academic.get<TSection>(`${baseAcademicPath}/sections/${id}`);
  };

  const createSection = (classId: string, request: TCreateSectionRequest) => {
    if (appWithMockData) {
      const now = new Date().toISOString();
      const row: TSection = {
        id: `section-${Date.now()}`,
        instituteId: "institute-1",
        classId,
        name: request.name,
        description: request.description,
        classTeacherId: request.classTeacherId,
        createdAt: now,
        updatedAt: now,
      };
      sectionListMock.push(row);
      return Promise.resolve(row);
    }
    return httpAPI.academic.post<TSection>(
      `${baseAcademicPath}/classes/${classId}/sections`,
      request,
    );
  };

  const updateSection = (id: string, request: TUpdateSectionRequest) => {
    if (appWithMockData) {
      const index = sectionListMock.findIndex((s) => s.id === id);
      if (index === -1) return Promise.reject(new Error("Section not found"));
      const prev = sectionListMock[index];
      const updated: TSection = {
        ...prev,
        ...request,
        classTeacherId:
          request.classTeacherId === null
            ? undefined
            : (request.classTeacherId ?? prev.classTeacherId),
        updatedAt: new Date().toISOString(),
      };
      sectionListMock[index] = updated;
      return Promise.resolve(updated);
    }
    return httpAPI.academic.patch<TSection>(
      `${baseAcademicPath}/sections/${id}`,
      request,
    );
  };

  const deleteSection = (id: string) => {
    if (appWithMockData) {
      const index = sectionListMock.findIndex((s) => s.id === id);
      if (index === -1) return Promise.reject(new Error("Section not found"));
      sectionListMock.splice(index, 1);
      delete sectionTeachersMock[id];
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(`${baseAcademicPath}/sections/${id}`);
  };

  const listClassSubjects = (classId: string) => {
    if (appWithMockData) {
      return Promise.resolve(
        classSubjectListMock.filter((r) => r.classId === classId),
      );
    }
    return httpAPI.academic.get<TClassSubject[]>(
      `${baseAcademicPath}/classes/${classId}/subjects`,
    );
  };

  const assignClassSubject = (
    classId: string,
    request: TAssignClassSubjectRequest,
  ) => {
    if (appWithMockData) {
      const now = new Date().toISOString();
      const sub = subjectListMock.find((s) => s.id === request.subjectId);
      const row: TClassSubject = {
        classId,
        subjectId: request.subjectId,
        subjectTeacherId: request.subjectTeacherId,
        subjectName: sub?.name,
        subjectCode: sub?.code,
        createdAt: now,
      };
      classSubjectListMock.push(row);
      return Promise.resolve(row);
    }
    return httpAPI.academic.post<TClassSubject>(
      `${baseAcademicPath}/classes/${classId}/subjects`,
      request,
    );
  };

  const updateClassSubject = (
    classId: string,
    subjectId: string,
    request: TUpdateClassSubjectRequest,
  ) => {
    if (appWithMockData) {
      const row = classSubjectListMock.find(
        (r) => r.classId === classId && r.subjectId === subjectId,
      );
      if (!row) return Promise.reject(new Error("Class-subject not found"));
      row.subjectTeacherId = request.subjectTeacherId ?? undefined;
      return Promise.resolve(row);
    }
    return httpAPI.academic.patch<TClassSubject>(
      `${baseAcademicPath}/classes/${classId}/subjects/${subjectId}`,
      request,
    );
  };

  const unassignClassSubject = (classId: string, subjectId: string) => {
    if (appWithMockData) {
      const index = classSubjectListMock.findIndex(
        (r) => r.classId === classId && r.subjectId === subjectId,
      );
      if (index === -1)
        return Promise.reject(new Error("Class-subject not found"));
      classSubjectListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(
      `${baseAcademicPath}/classes/${classId}/subjects/${subjectId}`,
    );
  };

  const listSectionTeachers = (sectionId: string) => {
    if (appWithMockData) {
      return Promise.resolve(sectionTeachersMock[sectionId] ?? []);
    }
    return httpAPI.academic.get<TSectionTeacher[]>(
      `${baseAcademicPath}/sections/${sectionId}/teachers`,
    );
  };

  const assignSectionTeacher = (
    sectionId: string,
    userId: string,
    roleLabel?: string,
  ) => {
    if (appWithMockData) {
      const row: TSectionTeacher = {
        userId,
        firstName: "Mock",
        lastName: "Teacher",
        role: "associated_teacher",
        label: roleLabel,
      };
      if (!sectionTeachersMock[sectionId]) sectionTeachersMock[sectionId] = [];
      sectionTeachersMock[sectionId].push(row);
      return Promise.resolve(row);
    }
    return httpAPI.academic.post<TSectionTeacher>(
      appendQuery(
        `${baseAcademicPath}/sections/${sectionId}/teachers/${userId}`,
        { role: roleLabel },
      ),
    );
  };

  const unassignSectionTeacher = (sectionId: string, userId: string) => {
    if (appWithMockData) {
      const list = sectionTeachersMock[sectionId];
      if (!list) return Promise.resolve(void 0);
      const idx = list.findIndex((t) => t.userId === userId);
      if (idx >= 0) list.splice(idx, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(
      `${baseAcademicPath}/sections/${sectionId}/teachers/${userId}`,
    );
  };

  const getSyllabi = (instituteId?: string) => {
    if (appWithMockData) {
      if (instituteId) {
        return Promise.resolve(
          syllabusListMock.filter(
            (syllabus) => syllabus.instituteId === instituteId,
          ),
        );
      }
      return Promise.resolve(syllabusListMock);
    }
    const path = instituteId
      ? `${baseAcademicPath}${adminSyllabiPath}?instituteId=${instituteId}`
      : `${baseAcademicPath}${adminSyllabiPath}`;
    return httpAPI.academic.get<TSyllabus[]>(path);
  };

  const getSyllabusById = (id: string) => {
    if (appWithMockData) {
      const syllabus = syllabusListMock.find((s) => s.id === id);
      if (!syllabus) return Promise.reject(new Error("Syllabus not found"));
      return Promise.resolve(syllabus);
    }
    return httpAPI.academic.get<TSyllabus>(
      `${baseAcademicPath}${adminSyllabiPath}/${id}`,
    );
  };

  const createSyllabus = (request: TCreateSyllabusRequest) => {
    if (appWithMockData) {
      const newSyllabus: TSyllabus = {
        id: `syllabus-${Date.now()}`,
        name: request.name,
        description: request.description,
        type: request.type,
        courseId: request.courseId,
        classId: request.classId,
        academicYear: request.academicYear,
        curriculumStructure: request.curriculumStructure,
        status: request.status || "active",
        instituteId: request.instituteId,
      };
      syllabusListMock.push(newSyllabus);
      return Promise.resolve(newSyllabus);
    }
    return httpAPI.academic.post<TSyllabus>(
      `${baseAcademicPath}${adminSyllabiPath}`,
      request,
    );
  };

  const updateSyllabus = (id: string, request: TUpdateSyllabusRequest) => {
    if (appWithMockData) {
      const index = syllabusListMock.findIndex((s) => s.id === id);
      if (index === -1) return Promise.reject(new Error("Syllabus not found"));

      const updatedSyllabus: TSyllabus = {
        ...syllabusListMock[index],
        ...request,
      };
      syllabusListMock[index] = updatedSyllabus;
      return Promise.resolve(updatedSyllabus);
    }
    return httpAPI.academic.patch<TSyllabus>(
      `${baseAcademicPath}${adminSyllabiPath}/${id}`,
      request,
    );
  };

  const deleteSyllabus = (id: string) => {
    if (appWithMockData) {
      const index = syllabusListMock.findIndex((s) => s.id === id);
      if (index === -1) return Promise.reject(new Error("Syllabus not found"));
      syllabusListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.academic.delete<void>(
      `${baseAcademicPath}${adminSyllabiPath}/${id}`,
    );
  };

  const getSyllabiByCourse = (courseId: string, instituteId?: string) => {
    if (appWithMockData) {
      return Promise.resolve(
        syllabusListMock.filter((s) => s.courseId === courseId),
      );
    }
    const params = new URLSearchParams();
    if (instituteId) params.append("instituteId", instituteId);
    params.append("courseId", courseId);
    return httpAPI.academic.get<TSyllabus[]>(
      `${baseAcademicPath}${adminSyllabiPath}?${params.toString()}`,
    );
  };

  const getSyllabiByClass = (classId: string, instituteId?: string) => {
    if (appWithMockData) {
      return Promise.resolve(
        syllabusListMock.filter((s) => s.classId === classId),
      );
    }
    const params = new URLSearchParams();
    if (instituteId) params.append("instituteId", instituteId);
    params.append("classId", classId);
    return httpAPI.academic.get<TSyllabus[]>(
      `${baseAcademicPath}${adminSyllabiPath}?${params.toString()}`,
    );
  };

  const assignSyllabus = (
    syllabusId: string,
    courseId?: string,
    classId?: string,
    academicYear?: string,
  ) => {
    if (appWithMockData) {
      const syllabus = syllabusListMock.find((s) => s.id === syllabusId);
      if (!syllabus) return Promise.reject(new Error("Syllabus not found"));
      const updatedSyllabus: TSyllabus = {
        ...syllabus,
        courseId: courseId || undefined,
        classId: classId || undefined,
        academicYear: academicYear || syllabus.academicYear,
        type: courseId ? "course" : "class",
      };
      const index = syllabusListMock.findIndex((s) => s.id === syllabusId);
      syllabusListMock[index] = updatedSyllabus;
      return Promise.resolve(updatedSyllabus);
    }
    return httpAPI.academic.patch<TSyllabus>(
      `${baseAcademicPath}${adminSyllabiPath}/${syllabusId}`,
      {
        type: courseId ? "course" : "class",
        courseId: courseId || null,
        classId: classId || null,
        academicYear,
      },
    );
  };

  const getInternalUsers = (search?: string): Promise<TInternalUser[]> => {
    if (appWithMockData) {
      return Promise.resolve([]);
    }
    const params = new URLSearchParams({ page: "1", limit: "500" });
    if (search) params.append("search", search);
    return httpAPI.users.get<{ data: TInternalUser[]; count: number }>(
      `/internal?${params.toString()}`,
    ).then((res: any) => res?.data ?? []);
  };

  const unassignSyllabus = (syllabusId: string) => {    if (appWithMockData) {
      const syllabus = syllabusListMock.find((s) => s.id === syllabusId);
      if (!syllabus) return Promise.reject(new Error("Syllabus not found"));
      const updatedSyllabus: TSyllabus = {
        ...syllabus,
        courseId: undefined,
        classId: undefined,
      };
      const index = syllabusListMock.findIndex((s) => s.id === syllabusId);
      syllabusListMock[index] = updatedSyllabus;
      return Promise.resolve(updatedSyllabus);
    }
    return httpAPI.academic.patch<TSyllabus>(
      `${baseAcademicPath}${adminSyllabiPath}/${syllabusId}`,
      {
        courseId: null,
        classId: null,
      },
    );
  };

  return {
    getAcademics,
    getCourses,
    getCourseById,
    createCourse,
    updateCourse,
    deleteCourse,
    getClasses,
    getClassById,
    createClass,
    updateClass,
    deleteClass,
    getGradeLevels,
    getGradeLevelById,
    createGradeLevel,
    updateGradeLevel,
    deleteGradeLevel,
    getSubjects,
    getSubjectById,
    createSubject,
    updateSubject,
    deleteSubject,
    listSectionsByClass,
    listSections,
    getSectionById,
    createSection,
    updateSection,
    deleteSection,
    listClassSubjects,
    assignClassSubject,
    updateClassSubject,
    unassignClassSubject,
    listSectionTeachers,
    assignSectionTeacher,
    unassignSectionTeacher,
    getSyllabi,
    getSyllabusById,
    createSyllabus,
    updateSyllabus,
    deleteSyllabus,
    getSyllabiByCourse,
    getSyllabiByClass,
    assignSyllabus,
    unassignSyllabus,
    getInternalUsers,
  };
};
